export type Range = {
  start: number;
  end: number;
};

export type BlockSnapshot = {
  id: string;
  start: number;
  size: number;
  allocated: boolean;
  generation: number;
};

export type PointerSnapshot = {
  toAddress: number;
  originalBlockId: string;
  originalGeneration: number;
};

export function boundaryCellForBlock(
  blockStart: number,
  blockSize: number,
  memorySize: number
): number | null {
  const boundary = blockStart + blockSize;
  if (boundary < 0 || boundary >= memorySize) {
    return null;
  }
  return boundary;
}

export function computeOverflowRange(
  blockStart: number,
  blockSize: number,
  writeLength: number,
  memorySize: number
): Range | null {
  if (writeLength <= blockSize) return null;
  const overflowStart = blockStart + blockSize;
  const overflowEnd = overflowStart + (writeLength - blockSize) - 1;
  const start = Math.max(0, overflowStart);
  const end = Math.min(memorySize - 1, overflowEnd);
  if (start > end) return null;
  return { start, end };
}

export function nextDanger(current: number, amount: number): number {
  return Math.min(100, Math.max(0, current + amount));
}

export function dangerLabelForValue(danger: number): 'Low' | 'Medium' | 'High' {
  if (danger >= 70) return 'High';
  if (danger >= 35) return 'Medium';
  return 'Low';
}

export function applyDoubleFreeTransition(
  block: BlockSnapshot,
  freeList: Range[]
): { nextAllocated: boolean; nextFreeList: Range[]; corruptionRange: Range | null } {
  const range = { start: block.start, end: block.start + block.size - 1 };
  const nextFreeList = [...freeList, range];

  if (block.allocated) {
    return {
      nextAllocated: false,
      nextFreeList,
      corruptionRange: null,
    };
  }

  return {
    nextAllocated: false,
    nextFreeList,
    corruptionRange: range,
  };
}

export function isDanglingPointer(
  pointer: PointerSnapshot,
  blockAtAddress: Pick<BlockSnapshot, 'id' | 'allocated' | 'generation'> | null
): boolean {
  if (!blockAtAddress) return true;
  return (
    !blockAtAddress.allocated ||
    blockAtAddress.id !== pointer.originalBlockId ||
    blockAtAddress.generation !== pointer.originalGeneration
  );
}
